// gitlab-merge-request-evidence-client.ts
//
// P7 §7 (review-remote-v1): thu MR + CI pipeline/job evidence từ GitLab REST
// v4 — KHÔNG có `glab` (D02), nên gọi thẳng REST với PAT đã lưu qua
// `git credential` (đọc qua `gitlab-pat-lookup.ts`, ghi qua
// `store-git-credential-for-host.ts` — chỉ import, không sửa).
//
// Ba luật:
//  1. Project id suy từ remote URL (`parseGitRemoteHost` + path), KHÔNG hard
//     code — path URL-encode nguyên vẹn (`nal/rd/avatar-cli` →
//     `nal%2Frd%2Favatar-cli`) theo GitLab REST convention.
//  2. Header `PRIVATE-TOKEN` — KHÔNG BAO GIỜ log giá trị PAT.
//  3. Thiếu PAT / host lạ (không phải GitLab tự host của org, tức parse được
//     nhưng chưa có credential) ⇒ trả `{kind:'resource_missing', reason}`,
//     KHÔNG throw — G1 amendment (P8) xử lý tiếp, không phải task_failed.
//
// Zod parse mọi response (input ngoài) — chỉ giữ field caller thực sự dùng,
// response GitLab thật có hàng chục field khác không cần thiết.
import { isSecureOrLoopbackBaseUrl } from "./gitlab-evidence-client-base-url-guard.js";
import {
  arraySchemaOrEmpty,
  type FetchLike,
  requestJson,
} from "./gitlab-evidence-request-helpers.js";
import {
  type GitlabJob,
  type GitlabMergeRequest,
  type GitlabPipeline,
  JobSchema,
  MergeRequestSchema,
  PipelineSchema,
} from "./gitlab-evidence-schemas.js";
import { lookupGitlabPat } from "./gitlab-pat-lookup.js";
import { selectMergeRequestPipeline } from "./gitlab-pipeline-ci-status.js";

export type { GitlabJob, GitlabMergeRequest, GitlabPipeline } from "./gitlab-evidence-schemas.js";

export interface GitlabEvidence {
  mergeRequest: GitlabMergeRequest;
  pipelines: GitlabPipeline[];
  /** The pipeline that speaks for CI (gitlab-pipeline-ci-status.ts); `jobs` are ITS jobs. */
  pipeline?: GitlabPipeline;
  jobs: GitlabJob[];
}

export type GitlabEvidenceResult =
  | { kind: "ok"; evidence: GitlabEvidence }
  | { kind: "resource_missing"; reason: string }
  | { kind: "not_found"; reason: string };

export interface GitlabEvidenceClientDeps {
  fetchImpl?: FetchLike;
  lookupPat?: (host: string) => Promise<{ found: boolean; token?: string }>;
  timeoutMs?: number;
  /**
   * M2 fix (code-reviewer-260909-p7-m3.md): official override for the
   * GitLab REST base URL — replaces the untyped `(deps as
   * {baseUrl}).baseUrl` backdoor. Tests point this at a local
   * `http://127.0.0.1:<port>` fake server; production never sets it (falls
   * back to `https://<host>`). Guarded below: a non-https, non-loopback
   * value is refused before the PAT header is ever attached to a request,
   * so a mistaken `baseUrl: "http://..."` can never downgrade the
   * `PRIVATE-TOKEN` header to plaintext transport.
   */
  baseUrl?: string;
}

// Re-exported so callers/tests importing from this module keep a single
// import path — the guard's implementation lives in its own file to keep
// this one under the 200-line convention.
export { isSecureOrLoopbackBaseUrl } from "./gitlab-evidence-client-base-url-guard.js";

/** `nal/rd/avatar-cli` (URL path segment sau host) → `nal%2Frd%2Favatar-cli`. */
export function projectPathFromRemoteUrl(remoteUrl: string): string | undefined {
  const trimmed = remoteUrl.trim();
  const schemeMatch = trimmed.match(/^(?:https?|ssh):\/\/(?:[^/@]+@)?[^/]+\/(.+?)(?:\.git)?\/?$/i);
  if (schemeMatch?.[1]) return schemeMatch[1];
  const sshMatch = trimmed.match(/^git@[^:]+:(.+?)(?:\.git)?\/?$/);
  if (sshMatch?.[1]) return sshMatch[1];
  return undefined;
}

function encodeProjectId(path: string): string {
  return encodeURIComponent(path);
}

/**
 * Fetch MR (theo `source_branch`, state=opened trước, fallback all nếu
 * không có opened — verdict-binding logic ở review-remote-adapter quyết
 * định SHA nào dùng) + pipelines của MR đó + jobs của pipeline mới nhất.
 * `host` phải đã parse từ remote URL của repo (KHÔNG nhận từ worker).
 */
export async function fetchGitlabMergeRequestEvidence(
  input: {
    host: string;
    remoteUrl: string;
    sourceBranch: string;
    baseUrl?: string;
    /** The commit the draft-MR push put on the branch: picks the pipeline of THAT commit. */
    pushedSha?: string;
  },
  deps: GitlabEvidenceClientDeps = {},
): Promise<GitlabEvidenceResult> {
  const fetchImpl = deps.fetchImpl ?? ((u, i) => fetch(u, i));
  const lookupPat = deps.lookupPat ?? ((h) => lookupGitlabPat(h));
  const timeoutMs = deps.timeoutMs ?? 10_000;

  const baseUrl = deps.baseUrl ?? input.baseUrl ?? `https://${input.host}`;
  // M2 fix: refuse an insecure baseUrl BEFORE the PAT is looked up or ever
  // attached to a request header — an http:// URL at a non-loopback host
  // would downgrade `PRIVATE-TOKEN` to plaintext transport.
  if (!isSecureOrLoopbackBaseUrl(baseUrl)) {
    return {
      kind: "resource_missing",
      reason: `insecure_base_url: baseUrl "${baseUrl}" không phải https:// và không phải loopback (127.0.0.1/localhost/[::1]) — từ chối để không gửi PAT qua http.`,
    };
  }

  const pat = await lookupPat(input.host);
  if (!pat.found || !pat.token) {
    return {
      kind: "resource_missing",
      reason: `Không có PAT đã lưu cho host "${input.host}" ⇒ không lấy được MR/CI evidence (cần \`avatar add repo\` hoặc lưu PAT trước).`,
    };
  }

  const projectPath = projectPathFromRemoteUrl(input.remoteUrl);
  if (projectPath === undefined) {
    return {
      kind: "resource_missing",
      reason: `Không parse được project path từ remote URL "${input.remoteUrl}".`,
    };
  }

  const projectId = encodeProjectId(projectPath);
  const token = pat.token;

  const mrUrl =
    `${baseUrl}/api/v4/projects/${projectId}/merge_requests` +
    `?source_branch=${encodeURIComponent(input.sourceBranch)}&state=opened`;
  let mrRes = await requestJson(fetchImpl, mrUrl, token, timeoutMs);
  let mrList = arraySchemaOrEmpty(MergeRequestSchema, mrRes);
  if (mrList.length === 0) {
    // Không có MR đang mở (đã merge/đóng) — fallback tìm theo mọi state để
    // vẫn cấp evidence cho verdict re-check (review-remote-adapter quyết
    // định có dùng được hay không dựa trên state + SHA).
    const mrUrlAll =
      `${baseUrl}/api/v4/projects/${projectId}/merge_requests` +
      `?source_branch=${encodeURIComponent(input.sourceBranch)}&state=all`;
    mrRes = await requestJson(fetchImpl, mrUrlAll, token, timeoutMs);
    mrList = arraySchemaOrEmpty(MergeRequestSchema, mrRes);
  }

  if (mrRes.status === -1) {
    return { kind: "resource_missing", reason: "GitLab REST timeout/network lỗi khi lấy MR." };
  }
  if (mrRes.status === 401 || mrRes.status === 403) {
    return {
      kind: "resource_missing",
      reason: `PAT cho "${input.host}" không đủ quyền (HTTP ${mrRes.status}).`,
    };
  }
  const mergeRequest = mrList[0];
  if (mergeRequest === undefined) {
    return {
      kind: "not_found",
      reason: `Không tìm thấy merge request nào cho source_branch="${input.sourceBranch}".`,
    };
  }

  // Bug N (P18): the LIST endpoint above omits `diff_refs`, and with it the
  // only field carrying the MR's real base oid (`diff_refs.base_sha`).
  // `merge_commit_sha` is a different commit entirely, and `target_branch` is
  // a name, not a SHA — using it is exactly what made the G3 binding check
  // reject every GitLab mission. GitLab returns `diff_refs` only from the
  // SINGLE-MR endpoint, so fetch that one MR to enrich what the list gave us.
  // Failure here is deliberately NON-fatal: evidence without `diff_refs`
  // still flows on, and the binding check then fails CLOSED on an empty base
  // rather than the caller losing the MR and CI evidence altogether.
  const singleMrUrl = `${baseUrl}/api/v4/projects/${projectId}/merge_requests/${mergeRequest.iid}`;
  const singleMrRes = await requestJson(fetchImpl, singleMrUrl, token, timeoutMs);
  const singleMr = MergeRequestSchema.safeParse(singleMrRes.body);
  const enrichedMergeRequest = singleMr.success ? singleMr.data : mergeRequest;

  const pipelinesUrl = `${baseUrl}/api/v4/projects/${projectId}/merge_requests/${mergeRequest.iid}/pipelines`;
  const pipelinesRes = await requestJson(fetchImpl, pipelinesUrl, token, timeoutMs);
  const pipelines = arraySchemaOrEmpty(PipelineSchema, pipelinesRes);

  const pipeline = selectMergeRequestPipeline(pipelines, input.pushedSha);
  let jobs: GitlabJob[] = [];
  if (pipeline !== undefined) {
    const jobsUrl = `${baseUrl}/api/v4/projects/${projectId}/pipelines/${pipeline.id}/jobs`;
    const jobsRes = await requestJson(fetchImpl, jobsUrl, token, timeoutMs);
    jobs = arraySchemaOrEmpty(JobSchema, jobsRes);
  }

  const evidence = { mergeRequest: enrichedMergeRequest, pipelines, pipeline, jobs };
  return { kind: "ok", evidence };
}
